import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '../../store/authStore'
import {
  getUserNotifications,
  getUnreadCount,
  markAsRead,
  markAllAsRead,
  deleteNotification,
  deleteAllRead,
  formatNotificationTime,
  getNotificationIcon,
  getNotificationColor,
  subscribeToNotifications,
  unsubscribeFromNotifications
} from '../../services/notificationService'

export default function Notifications() {
  const { user } = useAuthStore()
  const navigate = useNavigate()
  const [notifications, setNotifications] = useState([])
  const [unreadCount, setUnreadCount] = useState(0)
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState('all') // all, unread, read
  const [notificationChannel, setNotificationChannel] = useState(null)

  useEffect(() => {
    if (user?.id) {
      loadNotifications()
      loadUnreadCount()
      setupNotificationSubscription()
    }

    return () => {
      if (notificationChannel) {
        unsubscribeFromNotifications(notificationChannel)
      }
    }
  }, [user, filter])

  const loadNotifications = async () => {
    if (!user?.id) return

    setLoading(true)
    try {
      const { data, error } = await getUserNotifications(user.id, {
        limit: 100,
        unreadOnly: filter === 'unread'
      })
      if (!error && data) {
        let filteredData = data
        if (filter === 'read') {
          filteredData = data.filter(n => n.read)
        }
        setNotifications(filteredData)
      }
    } catch (err) {
      console.error('Error loading notifications:', err)
    } finally {
      setLoading(false)
    }
  }

  const loadUnreadCount = async () => {
    if (!user?.id) return

    try {
      const { count } = await getUnreadCount(user.id)
      setUnreadCount(count)
    } catch (err) {
      console.error('Error loading unread count:', err)
    }
  }

  const setupNotificationSubscription = () => {
    if (!user?.id) return

    const channel = subscribeToNotifications(user.id, (newNotification) => {
      setNotifications(prev => [newNotification, ...prev])
      setUnreadCount(prev => prev + 1)
    })

    setNotificationChannel(channel)
  }

  const handleMarkAsRead = async (notificationId) => {
    try {
      const { error } = await markAsRead(notificationId)
      if (!error) {
        setNotifications(prev =>
          prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
        )
        setUnreadCount(prev => Math.max(0, prev - 1))
      }
    } catch (err) {
      console.error('Error marking as read:', err)
    }
  }

  const handleMarkAllAsRead = async () => {
    if (!user?.id) return

    try {
      const { error } = await markAllAsRead(user.id)
      if (!error) {
        setNotifications(prev => prev.map(n => ({ ...n, read: true })))
        setUnreadCount(0)
      }
    } catch (err) {
      console.error('Error marking all as read:', err)
    }
  }

  const handleDelete = async (notificationId) => {
    try {
      const notification = notifications.find(n => n.id === notificationId)
      const { error } = await deleteNotification(notificationId)
      if (!error) {
        setNotifications(prev => prev.filter(n => n.id !== notificationId))
        if (notification && !notification.read) {
          setUnreadCount(prev => Math.max(0, prev - 1))
        }
      }
    } catch (err) {
      console.error('Error deleting notification:', err)
    }
  }

  const handleDeleteAllRead = async () => {
    if (!user?.id) return

    try {
      const { error } = await deleteAllRead(user.id)
      if (!error) {
        setNotifications(prev => prev.filter(n => !n.read))
      }
    } catch (err) {
      console.error('Error deleting read notifications:', err)
    }
  }

  const handleNotificationClick = async (notification) => {
    if (!notification.read) {
      await handleMarkAsRead(notification.id)
    }

    if (notification.action_url) {
      navigate(notification.action_url)
    }
  }

  const filteredNotifications = notifications

  return (
    <div className="p-8 max-w-5xl mx-auto w-full">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-text-light dark:text-white mb-2">Notifications</h1>
            <p className="text-text-muted-light dark:text-text-muted-dark">
              Stay updated with all your activities
            </p>
          </div>
          <div className="flex items-center gap-2">
            {unreadCount > 0 && (
              <button
                onClick={handleMarkAllAsRead}
                className="px-4 py-2 bg-primary hover:bg-primary-hover text-white rounded-xl font-medium transition-all text-sm"
              >
                Mark all as read
              </button>
            )}
            {notifications.some(n => n.read) && (
              <button
                onClick={handleDeleteAllRead}
                className="px-4 py-2 border border-border-light dark:border-border-dark hover:bg-gray-50 dark:hover:bg-white/5 rounded-xl font-medium transition-all text-sm"
              >
                Clear read
              </button>
            )}
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="flex items-center gap-2 bg-gray-100 dark:bg-gray-800 p-1 rounded-xl w-fit">
          <button
            onClick={() => setFilter('all')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              filter === 'all'
                ? 'bg-white dark:bg-surface-dark text-text-light dark:text-white shadow-sm'
                : 'text-text-muted-light dark:text-text-muted-dark hover:text-text-light dark:hover:text-white'
            }`}
          >
            All
            {notifications.length > 0 && (
              <span className="ml-2 text-xs opacity-60">({notifications.length})</span>
            )}
          </button>
          <button
            onClick={() => setFilter('unread')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              filter === 'unread'
                ? 'bg-white dark:bg-surface-dark text-text-light dark:text-white shadow-sm'
                : 'text-text-muted-light dark:text-text-muted-dark hover:text-text-light dark:hover:text-white'
            }`}
          >
            Unread
            {unreadCount > 0 && (
              <span className="ml-2 bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">{unreadCount}</span>
            )}
          </button>
          <button
            onClick={() => setFilter('read')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              filter === 'read'
                ? 'bg-white dark:bg-surface-dark text-text-light dark:text-white shadow-sm'
                : 'text-text-muted-light dark:text-text-muted-dark hover:text-text-light dark:hover:text-white'
            }`}
          >
            Read
          </button>
        </div>
      </div>

      {/* Notifications List */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
        </div>
      ) : filteredNotifications.length === 0 ? (
        <div className="bg-surface-light dark:bg-surface-dark rounded-2xl shadow-soft border border-border-light dark:border-border-dark p-20 text-center">
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
            <span className="material-symbols-outlined text-5xl text-text-muted-light dark:text-text-muted-dark">
              notifications_none
            </span>
          </div>
          <h3 className="text-xl font-bold text-text-light dark:text-white mb-2">
            {filter === 'unread' ? 'No unread notifications' : 
             filter === 'read' ? 'No read notifications' : 
             'No notifications yet'}
          </h3>
          <p className="text-text-muted-light dark:text-text-muted-dark">
            {filter === 'unread' ? "You're all caught up!" : 
             filter === 'read' ? 'Read notifications will appear here' :
             "You'll see notifications here when you have activity"}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredNotifications.map((notification) => (
            <div
              key={notification.id}
              onClick={() => handleNotificationClick(notification)}
              className={`group bg-surface-light dark:bg-surface-dark rounded-xl shadow-soft border border-border-light dark:border-border-dark p-5 hover:border-primary/50 transition-all cursor-pointer ${
                !notification.read ? 'ring-2 ring-primary/20' : ''
              }`}
            >
              <div className="flex items-start gap-4">
                {/* Icon */}
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center shrink-0 ${
                  !notification.read ? 'ring-2 ring-primary ring-offset-2 ring-offset-surface-light dark:ring-offset-surface-dark' : ''
                }`}>
                  <span className={`material-symbols-outlined text-xl ${getNotificationColor(notification.type)}`}>
                    {getNotificationIcon(notification.type)}
                  </span>
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-4 mb-2">
                    <div className="flex items-center gap-2">
                      <h3 className={`text-base font-semibold ${
                        !notification.read 
                          ? 'text-text-light dark:text-white' 
                          : 'text-text-muted-light dark:text-text-muted-dark'
                      }`}>
                        {notification.title}
                      </h3>
                      {!notification.read && (
                        <div className="w-2 h-2 rounded-full bg-primary"></div>
                      )}
                    </div>
                    <span className="text-xs text-text-muted-light dark:text-text-muted-dark shrink-0">
                      {formatNotificationTime(notification.created_at)}
                    </span>
                  </div>

                  <p className="text-sm text-text-muted-light dark:text-text-muted-dark mb-3">
                    {notification.message}
                  </p>

                  {/* Actions */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {notification.action_label && (
                        <span className="text-sm text-primary font-medium flex items-center gap-1">
                          {notification.action_label}
                          <span className="material-symbols-outlined text-sm">arrow_forward</span>
                        </span>
                      )}
                    </div>

                    <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                      {!notification.read && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            handleMarkAsRead(notification.id)
                          }}
                          className="p-2 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg transition-all"
                          title="Mark as read"
                        >
                          <span className="material-symbols-outlined text-sm text-green-500">done</span>
                        </button>
                      )}
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          handleDelete(notification.id)
                        }}
                        className="p-2 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-all"
                        title="Delete"
                      >
                        <span className="material-symbols-outlined text-sm text-red-500">delete</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
